import { zeroAddress, erc20Abi, Hex } from 'viem'
import { NetworkType, NetworkTypes } from '@provex/eip712-utils'
import { getEvmClientByChainId, getEvmExplorerUrl } from 'sacrifice-utils'
import { getChainById } from 'sacrifice-utils'
import { getCryptoIconUrl, getCoinInfo } from './crypto-icons'
import { getTokenImageUrl } from './token-image-mapping'
import { IMAGE_SERVER_URL } from '../config/app'

// Mapping of chain IDs to Blockchair coin identifiers for BIP32 networks
const BIP32_CHAIN_TO_BLOCKCHAIR: Record<number, string> = {
  0: 'bitcoin', // Bitcoin mainnet
  2: 'litecoin', // Litecoin mainnet
  3: 'dogecoin', // Dogecoin mainnet
  145: 'bitcoin-cash', // Bitcoin Cash mainnet
  5: 'dash', // Dash mainnet
  133: 'zcash', // Zcash mainnet
}

type RouteType = 'tx' | 'address' | 'token'

type JoinParams = { baseUrl?: string; chainId: number; type: RouteType; key: string }

class ExplorerUrlJoiner {
  constructor(
    private baseUrl?: string,
    private typeMapOverrides: Partial<Record<RouteType, string>> = {}
  ) {}

  join(jp: JoinParams): string {
    if (jp.type === 'token' && jp.key === zeroAddress) {
      return jp.baseUrl ?? this.baseUrl ?? ''
    }
    return `${jp.baseUrl ?? this.baseUrl}/${this.typeMapOverrides[jp.type] ?? jp.type}/${jp.key}`
  }
}

class Bip32ExplorerUrlJoiner extends ExplorerUrlJoiner {
  join(jp: JoinParams): string {
    const coinName = BIP32_CHAIN_TO_BLOCKCHAIR[jp.chainId]
    const baseUrl = `https://blockchair.com/${coinName}`
    return super.join({ ...jp, baseUrl })
  }
}

class EvmExplorerUrlJoiner extends ExplorerUrlJoiner {
  join(jp: JoinParams): string {
    return super.join({ ...jp, baseUrl: getEvmExplorerUrl(jp.chainId)! })
  }
}

const explorerUrlMap = new Map<NetworkType | `${NetworkType}:${number}`, ExplorerUrlJoiner>([
  [NetworkTypes.BIP32, new Bip32ExplorerUrlJoiner()],
  [
    NetworkTypes.MONERO,
    new ExplorerUrlJoiner('https://blockchair.com/monero', { tx: 'transaction' }),
  ],
  [NetworkTypes.EVM, new EvmExplorerUrlJoiner()],
  [NetworkTypes.CARDANO, new ExplorerUrlJoiner('https://cardanoscan.io', { tx: 'transaction' })],
  [NetworkTypes.SOLANA, new ExplorerUrlJoiner('https://explorer.solana.com')],
  [NetworkTypes.TVM, new ExplorerUrlJoiner('https://tronscan.org/#', { tx: 'transaction' })],
  [`${NetworkTypes.MEMO}:${148}`, new ExplorerUrlJoiner('https://stellar.expert/explorer/public')],
  [`${NetworkTypes.MEMO}:${144}`, new ExplorerUrlJoiner('https://xrpscan.com')],
])

export const getExplorerUrl = (
  networkType: NetworkType,
  chainId: number
): ExplorerUrlJoiner | null => {
  return explorerUrlMap.get(networkType) ?? explorerUrlMap.get(`${networkType}:${chainId}`) ?? null
}

/**
 * Get native token symbol for a given chain ID
 */
export const getNativeTokenSymbol = (chainId: number): string => {
  const chain = getChainById(chainId)
  if (chain && chain.nativeCurrency) {
    return chain.nativeCurrency.symbol
  }
  return ''
}

/**
 * Load EVM token symbols for a list of supported tokens
 */
export const loadEvmTokenSymbols = async (
  supportedTokens: Array<{ networkType: string; chainId: number; tokenAddress: string }>,
  chainId: number,
  existingSymbols: Record<string, string> = {}
): Promise<Record<string, string>> => {
  const client = getEvmClientByChainId(chainId)
  const newSymbols: Record<string, string> = {}

  for (const token of supportedTokens) {
    if (token.networkType === NetworkTypes.EVM) {
      const tokenKey = `${token.networkType}-${token.chainId}-${token.tokenAddress}`

      // Skip if already loaded
      if (existingSymbols[tokenKey]) {
        newSymbols[tokenKey] = existingSymbols[tokenKey]
        continue
      }

      try {
        // Handle native token (zero address)
        if (token.tokenAddress === zeroAddress) {
          newSymbols[tokenKey] = getNativeTokenSymbol(chainId)
        } else {
          // Get ERC20 token symbol
          const symbol = await client.readContract({
            address: token.tokenAddress as Hex,
            abi: erc20Abi,
            functionName: 'symbol',
          })
          newSymbols[tokenKey] = symbol
        }
      } catch (error) {
        console.warn(`Failed to load symbol for ${token.tokenAddress}:`, error)
        newSymbols[tokenKey] = ''
      }
    }
  }

  return newSymbols
}

/**
 * Get token icon URL based on network type and token address
 */
export const getTokenIconUrl = (
  networkType: string,
  chainId: number,
  tokenAddress: string
): string => {
  if (networkType === NetworkTypes.EVM) {
    // For native tokens (zero address), use network image
    if (tokenAddress === zeroAddress) {
      // Override Monad (chain ID 143) to use local image
      return chainId === 143
        ? '/monad_logo.png'
        : chainId === 4326
          ? '/megaeth_logo.svg'
          : `${IMAGE_SERVER_URL}/image/${chainId}`
    }
    // Use the token image mapping utility which handles cross-chain mappings
    return getTokenImageUrl(NetworkTypes.EVM, chainId, tokenAddress)
  }

  // For non-EVM tokens, return empty string (will be loaded separately)
  return ''
}

/**
 * Load token icon and symbol data for non-EVM tokens
 */
export const loadNonEvmTokenData = async (
  networkType: NetworkTypes,
  chainId: number
): Promise<{ iconUrl: string; symbol: string } | null> => {
  try {
    const [iconUrl, coinInfo] = await Promise.all([
      getCryptoIconUrl(networkType, chainId),
      getCoinInfo(networkType, chainId),
    ])

    if (iconUrl && coinInfo) {
      return {
        iconUrl,
        symbol: coinInfo.symbol,
      }
    }
  } catch (error) {
    console.warn(`Failed to load non-EVM token data for ${networkType}:${chainId}:`, error)
  }

  return null
}
